import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { authApi } from '../lib/api';

type State = 'working' | 'done' | 'error' | 'missing';

/**
 * Cible du lien reçu par courriel : /verify-email?token=...
 *
 * En cas d'échec (lien expiré, déjà utilisé), un renvoi est proposé sur place :
 * sans cela une personne au lien périmé n'aurait aucun moyen d'avancer.
 */
export function VerifyEmailPage() {
  // useSearchParams n'est pas disponible dans cette version de react-router-dom.
  const token = new URLSearchParams(window.location.search).get('token') || '';

  const [state, setState] = useState<State>(token ? 'working' : 'missing');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    authApi
      .confirmEmail(token)
      .then(() => {
        if (!cancelled) setState('done');
      })
      .catch((err) => {
        if (cancelled) return;
        setState('error');
        setMessage(err instanceof Error ? err.message : 'Vérification impossible');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function resend(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setNotice('');
    try {
      await authApi.resendVerification(email);
      setNotice(
        "Si un compte non activé existe pour cette adresse, un nouveau lien vient d'être envoyé.",
      );
    } catch {
      // Réponse volontairement identique en cas d'erreur : le formulaire ne
      // doit pas permettre de découvrir quelles adresses sont enregistrées.
      setNotice(
        "Si un compte non activé existe pour cette adresse, un nouveau lien vient d'être envoyé.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <section className="auth-card">
        <div className="auth-brand">
          <img
            src="/logo-eduversa-300.png"
            alt="EDUVERSA — Learn without boundaries"
            className="auth-logo"
          />
        </div>

        {state === 'working' && (
          <>
            <h1>Activation…</h1>
            <p>Vérification de votre lien en cours.</p>
          </>
        )}

        {state === 'done' && (
          <>
            <h1>Compte activé</h1>
            <p>Votre adresse est confirmée. Vous pouvez maintenant vous connecter.</p>
            <Link className="button primary" to="/login">
              Aller à la connexion
            </Link>
          </>
        )}

        {(state === 'error' || state === 'missing') && (
          <>
            <h1>Lien invalide</h1>
            <p>
              {state === 'missing'
                ? "Ce lien ne contient aucun code d'activation."
                : message || 'Ce lien a expiré ou a déjà été utilisé.'}
            </p>
            <p>Indiquez votre adresse pour recevoir un nouveau lien.</p>

            {notice && <div className="notice">{notice}</div>}

            <form onSubmit={resend}>
              <label>
                Email
                <input
                  type="email"
                  value={email}
                  onChange={(e: any) => setEmail(e.target.value)}
                  required
                />
              </label>
              <button className="button primary" disabled={busy}>
                {busy ? 'Envoi…' : 'Renvoyer le lien'}
              </button>
              <Link className="button ghost" to="/login">
                Retour à la connexion
              </Link>
            </form>
          </>
        )}
      </section>
    </div>
  );
}
